import type { RouterInputs } from '@/utils/api';

import type { CallLabelKey } from '@poseidon/types/svr/callReview';
import { CALL_LABEL_KEYS, CALL_LABEL_VALUE_NAMES, isCallLabelValue } from '@poseidon/types/svr/callReview';

/**
 * Call Review's filters ↔ the URL ↔ the list query, plus the plain-words summary under the bar.
 * Pure — the page, the filter bar and the Cypress specs share it.
 */

export const CALL_REVIEW_PERIODS = ['7', '14', '30', '90', 'all'] as const;
export type CallReviewPeriod = (typeof CALL_REVIEW_PERIODS)[number];
export const CALL_REVIEW_LENGTHS = ['under_1m', '1_5m', 'over_5m'] as const;
export type CallReviewLength = (typeof CALL_REVIEW_LENGTHS)[number];
export const CALL_REVIEW_SCORE_BANDS = ['low', 'mid', 'top'] as const;
export type CallReviewScoreBand = (typeof CALL_REVIEW_SCORE_BANDS)[number];

/** What the filter bar edits. '' / 'any' / [] mean "no filter". */
export type CallReviewFilterState = {
  sort: 'best' | 'recent';
  periodDays: CallReviewPeriod;
  repUserId: string;
  disposition: string;
  duration: 'any' | CallReviewLength;
  campaignId: string;
  scoreBand: 'any' | CallReviewScoreBand;
  connectedOnly: boolean;
  hasRecording: boolean;
  hasTranscript: boolean;
  /** Any of these values per label (several outcomes can be on at once). */
  labels: Record<CallLabelKey, string[]>;
  q: string;
};

/** "Best calls": connected calls in the last 14 days, highest quality score first. */
export const DEFAULT_CALL_REVIEW_FILTERS: CallReviewFilterState = {
  sort: 'best',
  periodDays: '14',
  repUserId: '',
  disposition: '',
  duration: 'any',
  campaignId: '',
  scoreBand: 'any',
  connectedOnly: true,
  hasRecording: false,
  hasTranscript: false,
  labels: { outcome: [], objection: [], next_step: [], sentiment: [] },
  q: '',
};

export const CALL_REVIEW_PERIOD_LABELS: Record<CallReviewPeriod, string> = {
  '7': 'Last 7 days',
  '14': 'Last 14 days',
  '30': 'Last 30 days',
  '90': 'Last 90 days',
  all: 'All time',
};

/** Segment label + what it means (tooltip / summary). Cutoffs match `pgCallReviewList`. */
export const CALL_REVIEW_LENGTH_LABELS: Record<CallReviewLength, { short: string; long: string }> = {
  under_1m: { short: '<1m', long: 'under 1 min' },
  '1_5m': { short: '1–5m', long: '1–5 min' },
  over_5m: { short: '5m+', long: '5 min or longer' },
};

export const CALL_REVIEW_SCORE_LABELS: Record<CallReviewScoreBand, { short: string; long: string }> = {
  low: { short: 'Low', long: 'score under 40' },
  mid: { short: 'Mid', long: 'score 40–69' },
  top: { short: 'High', long: 'score 70+' },
};

/**
 * The URL params, one per filter. Absent = that filter's default, so a bare `/call-review` (or an
 * older `?call=…` link) opens the default view. `reviewAll` turns "connected only" OFF, since
 * connected-only is the default.
 */
export type CallReviewFilterQuery = {
  reviewSort?: 'best' | 'recent';
  reviewDays?: CallReviewPeriod;
  reviewRep?: string;
  reviewDisposition?: string;
  reviewCampaign?: string;
  reviewLength?: CallReviewLength;
  reviewScore?: CallReviewScoreBand;
  reviewAll?: boolean;
  reviewRecording?: boolean;
  reviewTranscript?: boolean;
  reviewOutcome?: string[];
  reviewObjection?: string[];
  reviewNextStep?: string;
  reviewSentiment?: string[];
  reviewQ?: string;
};

const validLabels = (key: CallLabelKey, values: string[] | undefined) =>
  (values ?? []).filter((v) => isCallLabelValue(key, v));

/** URL → filter state. Unknown label values drop out. */
export function callReviewFiltersFromQuery(q: CallReviewFilterQuery): CallReviewFilterState {
  const d = DEFAULT_CALL_REVIEW_FILTERS;
  return {
    sort: q.reviewSort ?? d.sort,
    periodDays: q.reviewDays ?? d.periodDays,
    repUserId: q.reviewRep ?? '',
    disposition: q.reviewDisposition ?? '',
    duration: q.reviewLength ?? 'any',
    campaignId: q.reviewCampaign ?? '',
    scoreBand: q.reviewScore ?? 'any',
    connectedOnly: q.reviewAll !== true,
    hasRecording: q.reviewRecording === true,
    hasTranscript: q.reviewTranscript === true,
    labels: {
      outcome: validLabels('outcome', q.reviewOutcome),
      objection: validLabels('objection', q.reviewObjection),
      next_step: validLabels('next_step', q.reviewNextStep != null ? [q.reviewNextStep] : undefined),
      sentiment: validLabels('sentiment', q.reviewSentiment),
    },
    q: q.reviewQ ?? '',
  };
}

type CallReviewQueryPatch = { [K in keyof Required<CallReviewFilterQuery>]: CallReviewFilterQuery[K] };

/** Filter state → URL patch. Every key is present (undefined clears it), so a patch fully replaces. */
export function callReviewFiltersToQuery(s: CallReviewFilterState): CallReviewQueryPatch {
  const q = s.q.trim();
  return {
    reviewSort: s.sort === 'best' ? undefined : s.sort,
    reviewDays: s.periodDays === '14' ? undefined : s.periodDays,
    reviewRep: s.repUserId || undefined,
    reviewDisposition: s.disposition || undefined,
    reviewCampaign: s.campaignId || undefined,
    reviewLength: s.duration === 'any' ? undefined : s.duration,
    reviewScore: s.scoreBand === 'any' ? undefined : s.scoreBand,
    reviewAll: s.connectedOnly ? undefined : true,
    reviewRecording: s.hasRecording ? true : undefined,
    reviewTranscript: s.hasTranscript ? true : undefined,
    reviewOutcome: s.labels.outcome.length > 0 ? s.labels.outcome : undefined,
    reviewObjection: s.labels.objection.length > 0 ? s.labels.objection : undefined,
    reviewNextStep: s.labels.next_step[0],
    reviewSentiment: s.labels.sentiment.length > 0 ? s.labels.sentiment : undefined,
    reviewQ: q !== '' ? q : undefined,
  };
}

/** Whether the view is the default "Best calls" view (search included). */
export function isDefaultCallReviewView(s: CallReviewFilterState): boolean {
  return Object.values(callReviewFiltersToQuery(s)).every((v) => v === undefined);
}

/** How many "More filters" (rep, campaign, disposition, the three toggles) differ from the default. */
export function countMoreFilters(s: CallReviewFilterState): number {
  return [
    s.repUserId !== '',
    s.campaignId !== '',
    s.disposition !== '',
    !s.connectedOnly,
    s.hasRecording,
    s.hasTranscript,
  ].filter(Boolean).length;
}

/** Toggle one value in a list (multi-select chips / icon toggles). */
export function toggleValue(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

/** A label value's plain name; an objection of "none" reads "No objection" out of context. */
export function labelValueName(key: CallLabelKey, v: string): string {
  if (key === 'objection' && v === 'none') return 'No objection';
  return CALL_LABEL_VALUE_NAMES[v] ?? v;
}

/** "Meeting booked, Callback" — the full names, as picked. */
export function labelValuesText(key: CallLabelKey, values: string[]): string {
  return values.map((v) => labelValueName(key, v)).join(', ');
}

const anyOf = (key: CallLabelKey, values: string[]) =>
  values
    .map((v) => labelValueName(key, v))
    .join(' or ')
    .toLowerCase();

/**
 * The current view in plain words, one phrase per active filter:
 * "Connected calls · last 14 days · 1–5 min · got a next step · best first".
 */
export function callReviewSummary(s: CallReviewFilterState, names: { rep?: string; campaign?: string } = {}): string[] {
  const parts: string[] = [s.connectedOnly ? 'Connected calls' : 'All calls, connected or not'];
  parts.push(s.periodDays === 'all' ? 'all time' : CALL_REVIEW_PERIOD_LABELS[s.periodDays].toLowerCase());
  if (s.repUserId !== '') parts.push(`by ${names.rep ?? 'one rep'}`);
  if (s.duration !== 'any') parts.push(CALL_REVIEW_LENGTH_LABELS[s.duration].long);
  if (s.scoreBand !== 'any') parts.push(CALL_REVIEW_SCORE_LABELS[s.scoreBand].long);
  if (s.labels.sentiment.length > 0) parts.push(`${anyOf('sentiment', s.labels.sentiment)} tone`);
  const nextStep = s.labels.next_step[0];
  if (nextStep === 'yes') parts.push('got a next step');
  if (nextStep === 'no') parts.push('no next step');
  if (s.labels.outcome.length > 0) parts.push(anyOf('outcome', s.labels.outcome));
  if (s.labels.objection.length > 0) {
    const named = s.labels.objection.filter((v) => v !== 'none');
    parts.push(
      [
        ...(s.labels.objection.includes('none') ? ['no objection'] : []),
        ...(named.length > 0 ? [`objection: ${anyOf('objection', named)}`] : []),
      ].join(' or '),
    );
  }
  if (s.campaignId !== '') parts.push(`in ${names.campaign ?? 'one campaign'}`);
  if (s.disposition !== '') parts.push(`marked ${s.disposition}`);
  if (s.hasRecording) parts.push('with a recording');
  if (s.hasTranscript) parts.push('with a transcript');
  const q = s.q.trim();
  if (q !== '') parts.push(`mentioning “${q}”`);
  parts.push(s.sort === 'best' ? 'best first' : 'newest first');
  return parts;
}

type ListInput = RouterInputs['callReview']['list'];

/** Filter-bar state → the list query input. `now` injectable for tests. */
export function toCallReviewListInput(
  teamId: string,
  s: CallReviewFilterState,
  now: Date = new Date(),
): Omit<ListInput, 'cursor'> {
  const from =
    s.periodDays === 'all' ? undefined : new Date(now.getTime() - Number(s.periodDays) * 86_400_000).toISOString();
  const labels: NonNullable<ListInput['filters']['labels']> = {};
  for (const key of CALL_LABEL_KEYS) {
    if (s.labels[key].length > 0) labels[key] = s.labels[key];
  }
  return {
    teamId,
    sort: s.sort,
    limit: 30,
    filters: {
      from,
      repUserId: s.repUserId || undefined,
      dispositions: s.disposition !== '' ? [s.disposition] : undefined,
      duration: s.duration,
      campaignId: s.campaignId || undefined,
      scoreBand: s.scoreBand,
      connectedOnly: s.connectedOnly || undefined,
      hasRecording: s.hasRecording || undefined,
      hasTranscript: s.hasTranscript || undefined,
      labels,
      q: s.q.trim() || undefined,
    },
  };
}

export function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
